import { useEffect, useState } from "react";
import { MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { brl, fmtDateTime, imgUrl, waLink } from "@/lib/api";
import { PROFILE, todayIdx, myOrders } from "@/lib/shop";
import { cn } from "@/lib/utils";

const DAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const STATUS = { recebido: "Recebido", confirmado: "Confirmado", preparo: "Em preparação", pronto: "Pronto", entrega: "Saiu para entrega", entregue: "Entregue", concluido: "Entregue", cancelado: "Cancelado" };
const fmtCnpj = (d) => d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");

export function InfoDialog({ open, onOpenChange, st }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto bg-white text-slate-900" data-testid="shop-info-modal">
        <DialogHeader><DialogTitle className="text-slate-900">{st.name}</DialogTitle></DialogHeader>
        <Tabs defaultValue="sobre">
          <TabsList className="grid w-full grid-cols-3"><TabsTrigger value="sobre">Sobre</TabsTrigger><TabsTrigger value="horario" data-testid="shop-info-hours-tab">Horário</TabsTrigger><TabsTrigger value="pagamento" data-testid="shop-info-pay-tab">Pagamento</TabsTrigger></TabsList>
          <TabsContent value="sobre" className="mt-4 space-y-3 text-sm">
            {st.logo_url && <img src={imgUrl(st.logo_url)} alt="Logo" className="h-20 w-20 rounded-xl bg-black object-cover" />}
            {st.telefone && <a href={waLink(st.telefone, "Olá! Vim pelo delivery.")} target="_blank" rel="noreferrer" className="flex items-center gap-2 font-medium text-emerald-700"><Phone className="h-4 w-4" />{st.telefone} (WhatsApp)</a>}
            {st.address && <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0" />{st.address}</p>}
            {st.cnpj && <p className="text-slate-500">CNPJ: {fmtCnpj(st.cnpj)}</p>}
          </TabsContent>
          <TabsContent value="horario" className="mt-4">
            <ul className="divide-y text-sm">{DAYS.map((d, i) => {
              const h = st.opening_hours?.[i];
              return <li key={d} className={cn("flex justify-between py-2", i === todayIdx() && "font-bold")}><span>{d}</span><span>{h?.enabled ? `${h.open} às ${h.close}` : "Fechado"}</span></li>;
            })}</ul>
          </TabsContent>
          <TabsContent value="pagamento" className="mt-4 space-y-2 text-sm">
            {(st.pay_methods || []).map((m) => <p key={m.id} className="rounded-lg border px-3 py-2">{m.label}{m.brands?.length ? <span className="block text-xs text-slate-500">{m.brands.join(", ")}</span> : null}</p>)}
            {st.online?.pix && <p className="rounded-lg border px-3 py-2">Pix online (QR Code)</p>}
            {st.online?.checkout && <p className="rounded-lg border px-3 py-2">Mercado Pago (cartão ou Pix)</p>}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

export function OrdersView({ http, me, onOpen, onLogin, color, slug }) {
  const [rows, setRows] = useState(null);
  const [phone, setPhone] = useState(JSON.parse(localStorage.getItem(PROFILE) || "{}").phone || "");
  useEffect(() => { if (me) http.get("/account/orders").then((r) => setRows(r.data)).catch(() => setRows([])); }, [me, http]);
  const search = () => http.get(`/my-orders?${myOrders.query(slug, phone)}`).then((r) => setRows(r.data));
  return (
    <section className="space-y-4" data-testid="shop-orders-view">
      <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Meus pedidos</h2>
      {!me && (
        <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm text-slate-600">Busque pelo celular usado no pedido (mostra os pedidos feitos neste aparelho) ou <button onClick={onLogin} className="font-semibold" style={{ color }}>entre na sua conta</button>.</p>
          <div className="flex gap-2"><Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Seu celular" inputMode="tel" className="bg-white" data-testid="shop-orders-phone" /><Button onClick={search} className="text-white" style={{ background: color }} data-testid="shop-orders-search">Buscar</Button></div>
        </div>
      )}
      <ul className="space-y-2">{(rows || []).map((o) => (
        <li key={o.id}>
          <button onClick={() => onOpen(o.id)} className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition-shadow hover:shadow-md" data-testid={`shop-order-${o.number}`}>
            <span><b>Pedido #{o.number}</b><span className="block text-xs text-slate-500">{fmtDateTime(o.created_at)}{o.items ? ` · ${o.items.slice(0, 3).join(", ")}` : ""}</span></span>
            <span className="text-right text-sm"><b>{brl(o.total)}</b><span className="block text-xs" style={{ color }}>{STATUS[o.status] || o.status}</span></span>
          </button>
        </li>
      ))}</ul>
      {rows?.length === 0 && <p className="text-sm text-slate-500" data-testid="shop-orders-empty">Nenhum pedido encontrado.</p>}
    </section>
  );
}
